/* Mobile menu accessibility (audit issues 237-243, 280, 281).
 *
 * Adds dialog semantics, focus trap, Escape-to-close, focus management
 * and aria-expanded synchronization for the mobile navigation drawer.
 *
 * The drawer is the existing <nav class="header-nav">; on mobile it slides
 * in when <body> gets the "nav-active" class. Sub-menus inside the drawer
 * are toggled by adding "header-nav-item--sub-active" to the parent <li>
 * (existing behavior in scripts.js, which we observe and mirror in ARIA).
 */
(function () {
  'use strict';

  var body = document.body;
  var hamburger = document.querySelector('.header-hamburger');
  var dialog = document.getElementById('mobile-menu-dialog');

  if (!body || !dialog) return;

  var closeButton = dialog.querySelector('[data-mobile-menu-close]');
  var mql = window.matchMedia('(max-width: 959px)');
  var lastFocused = null;

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])',
    'audio[controls]',
    'video[controls]',
    'details summary'
  ].join(',');

  function isVisible(el) {
    if (!el) return false;
    if (el.hasAttribute('hidden')) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return el.offsetParent !== null || el === document.activeElement;
  }

  function getFocusable() {
    return Array.prototype.slice
      .call(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(isVisible);
  }

  function applyDialogAttrs() {
    var isMobile = mql.matches;
    if (isMobile) {
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('tabindex', '-1');
      if (!body.classList.contains('nav-active')) {
        dialog.setAttribute('aria-hidden', 'true');
      } else {
        dialog.removeAttribute('aria-hidden');
      }
    } else {
      dialog.removeAttribute('role');
      dialog.removeAttribute('aria-modal');
      dialog.removeAttribute('tabindex');
      dialog.removeAttribute('aria-hidden');
    }
  }

  function syncDialogHidden() {
    if (!mql.matches) {
      dialog.removeAttribute('aria-hidden');
      return;
    }
    if (body.classList.contains('nav-active')) {
      dialog.removeAttribute('aria-hidden');
    } else {
      dialog.setAttribute('aria-hidden', 'true');
    }
  }

  function moveFocusIn() {
    if (!mql.matches) return;
    /* Defer slightly so any opening animation doesn't steal focus. */
    window.setTimeout(function () {
      if (closeButton && isVisible(closeButton)) {
        closeButton.focus();
      } else {
        var focusable = getFocusable();
        if (focusable.length) {
          focusable[0].focus();
        } else {
          dialog.focus();
        }
      }
    }, 30);
  }

  function returnFocus() {
    if (!mql.matches) return;
    var target = lastFocused && typeof lastFocused.focus === 'function' ? lastFocused : hamburger;
    lastFocused = null;
    if (target) {
      try {
        target.focus();
      } catch (e) {
        /* element may have been removed */
      }
    }
  }

  function closeMenu() {
    body.classList.remove('nav-active');
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusable = getFocusable();
    if (!focusable.length) {
      e.preventDefault();
      dialog.focus();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || !dialog.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function onKeydown(e) {
    if (!body.classList.contains('nav-active') || !mql.matches) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeMenu();
      return;
    }
    trapFocus(e);
  }

  if (closeButton) {
    closeButton.addEventListener('click', function (e) {
      e.preventDefault();
      closeMenu();
    });
  }

  if (hamburger) {
    hamburger.addEventListener('click', function () {
      /* Capture trigger before existing scripts.js toggles the class. */
      lastFocused = hamburger;
    });
  }

  document.addEventListener('keydown', onKeydown);

  /* Watch for nav-active toggling on <body>. */
  var bodyObserver = new MutationObserver(function () {
    syncDialogHidden();
    if (body.classList.contains('nav-active')) {
      moveFocusIn();
    } else {
      returnFocus();
    }
  });
  bodyObserver.observe(body, { attributes: true, attributeFilter: ['class'] });

  /* Initial setup. */
  applyDialogAttrs();
  if (mql.addEventListener) {
    mql.addEventListener('change', applyDialogAttrs);
  } else if (mql.addListener) {
    mql.addListener(applyDialogAttrs);
  }

  /* ------------------------------------------------------------------ *
   * 1. Mirror header-nav-item--sub-active → aria-expanded.
   * 2. Keep sub-nav links OUT of the tab order (inert) while collapsed.
   * ------------------------------------------------------------------ */
  var allSubTriggers = document.querySelectorAll('.header-nav-anchor--sub-trigger');
  allSubTriggers.forEach(function (trigger) {
    var li = trigger.closest('.header-nav-item') || trigger.closest('.header-sub-nav-item');
    if (!li) return;

    var controlledId = trigger.getAttribute('aria-controls');
    var subNav = controlledId ? document.getElementById(controlledId) : li.querySelector('.header-sub-nav');

    function syncState() {
      var open = li.classList.contains('header-nav-item--sub-active');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (subNav) {
        if (open) {
          subNav.removeAttribute('inert');
        } else {
          subNav.setAttribute('inert', '');
        }
      }
    }

    syncState();
    new MutationObserver(syncState).observe(li, {
      attributes: true,
      attributeFilter: ['class']
    });
  });

  /* ------------------------------------------------------------------ *
   * Desktop sub-nav: click/keyboard toggle + hover sync + close helpers.
   *
   * The desktop nav relies solely on CSS :hover — buttons have no click
   * handler, so keyboard users cannot open sub-menus at all.  We add one
   * here, scoped to the desktop <ul> only, to avoid interfering with the
   * existing scripts.js mobile handler.
   * ------------------------------------------------------------------ */
  var desktopNav = document.querySelector('.header__menu--desktop');
  if (desktopNav) {
    var desktopHasSubItems = desktopNav.querySelectorAll('.header-nav-item--has-sub');

    function closeAllDesktopSubs(except) {
      desktopHasSubItems.forEach(function (item) {
        if (item !== except) item.classList.remove('header-nav-item--sub-active');
      });
    }

    desktopHasSubItems.forEach(function (li) {
      var trigger = li.querySelector(':scope > .header-nav-anchor--sub-trigger');
      if (!trigger) return;

      function toggleDesktopSub() {
        var isOpen = li.classList.contains('header-nav-item--sub-active');
        closeAllDesktopSubs(li);
        li.classList.toggle('header-nav-item--sub-active', !isOpen);
      }

      /* Mouse click. */
      trigger.addEventListener('click', toggleDesktopSub);

      /* Keyboard: add a dedicated keydown handler so that even if scripts.js
       * calls stopImmediatePropagation() on the 'click' event, Enter and
       * Space still activate the button.  keydown is a separate event and
       * cannot be blocked by a click handler on the same element. */
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleDesktopSub();
        }
        /* Escape while focused on the trigger itself closes the sub-nav. */
        if ((e.key === 'Escape' || e.key === 'Esc') &&
            li.classList.contains('header-nav-item--sub-active')) {
          e.stopPropagation();
          li.classList.remove('header-nav-item--sub-active');
        }
      });

      /* Escape from anywhere inside the open sub-nav returns focus to trigger. */
      li.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') &&
            li.classList.contains('header-nav-item--sub-active') &&
            e.target !== trigger) {
          e.stopPropagation();
          li.classList.remove('header-nav-item--sub-active');
          trigger.focus();
        }
      });

      /* Hover: lift inert so mouse-then-tab works naturally. */
      li.addEventListener('mouseenter', function () {
        closeAllDesktopSubs(li);
        li.classList.add('header-nav-item--sub-active');
      });
      li.addEventListener('mouseleave', function () {
        /* Delay so focus can land inside before we collapse. */
        window.setTimeout(function () {
          if (!li.contains(document.activeElement)) {
            li.classList.remove('header-nav-item--sub-active');
          }
        }, 100);
      });
    });

    /* Click outside closes all desktop sub-navs. */
    document.addEventListener('click', function (e) {
      if (!desktopNav.contains(e.target)) {
        closeAllDesktopSubs(null);
      }
    });
  }
})();
